// Offer Follow-up Dashboard - Staff view for tracking offer responses and customer engagement
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import OffersModuleLayout from "./OffersModuleLayout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  Users,
  Mail,
  Phone,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Star,
  Send,
  Eye
} from "lucide-react";


const StatusBadge = ({ status }) => {
  const styles = {
    pending_response: "bg-yellow-100 text-yellow-700",
    reminder_sent: "bg-blue-100 text-blue-700",
    expired: "bg-gray-100 text-gray-600",
    confirmed: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    survey_sent: "bg-purple-100 text-purple-700",
    completed: "bg-emerald-100 text-emerald-700"
  };

  const labels = {
    pending_response: "Awaiting Response",
    reminder_sent: "Reminder Sent",
    expired: "Expired",
    confirmed: "Confirmed",
    rejected: "Rejected",
    survey_sent: "Survey Sent",
    completed: "Completed"
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || "bg-gray-100"}`}>
      {labels[status] || status}
    </span>
  );
};

const RatingDisplay = ({ rating }) => {
  const emojis = ["", "😞", "😕", "😐", "🙂", "😄"];
  return (
    <span className="flex items-center gap-1">
      <span className="text-xl">{emojis[rating] || "—"}</span>
      <span className="text-sm text-gray-500">({rating}/5)</span>
    </span>
  );
};

export default function OfferFollowupDashboard() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("analytics");
  
  // Analytics data
  const [followupAnalytics, setFollowupAnalytics] = useState(null);
  const [engagementAnalytics, setEngagementAnalytics] = useState(null);
  
  // List data
  const [followups, setFollowups] = useState([]);
  const [customers, setCustomers] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, engagementRes, followupsRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/offer-followups/analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/customer-engagement/analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/offer-followups?limit=20`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/customer-engagement?limit=20`, { headers: getAuthHeaders() })
      ]);
      
      setFollowupAnalytics(analyticsRes.data);
      setEngagementAnalytics(engagementRes.data);
      setFollowups(followupsRes.data.followups);
      setCustomers(customersRes.data.customers);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <OffersModuleLayout>
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Offer Follow-up & Engagement</h1>
            <p className="text-muted-foreground">Track offer responses and customer re-engagement</p>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "analytics" ? "default" : "outline"}
            onClick={() => setActiveTab("analytics")}
            className={activeTab === "analytics" ? "bg-primary" : ""}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </Button>
          <Button
            variant={activeTab === "followups" ? "default" : "outline"}
            onClick={() => setActiveTab("followups")}
            className={activeTab === "followups" ? "bg-primary" : ""}
          >
            <Clock className="h-4 w-4 mr-2" />
            Offer Follow-ups
          </Button>
          <Button
            variant={activeTab === "engagement" ? "default" : "outline"}
            onClick={() => setActiveTab("engagement")}
            className={activeTab === "engagement" ? "bg-primary" : ""}
          >
            <Users className="h-4 w-4 mr-2" />
            Customer Engagement
          </Button>
        </div>

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Offer Follow-up Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-surface p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {followupAnalytics?.status_counts?.pending_response || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-surface p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Send className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {followupAnalytics?.status_counts?.reminder_sent || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Reminded</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-surface p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {followupAnalytics?.status_counts?.confirmed || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Confirmed</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card-surface p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {followupAnalytics?.status_counts?.rejected || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Rejected</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card-surface p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {followupAnalytics?.status_counts?.completed || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Surveyed</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="card-surface p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Star className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {followupAnalytics?.average_rating || "—"}
                    </p>
                    <p className="text-xs text-muted-foreground">Avg Rating</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Rejection Reasons & Survey Ratings */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Rejection Reasons */}
              <div className="card-surface p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  Rejection Reasons
                </h3>
                {Object.keys(followupAnalytics?.rejection_reasons || {}).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No rejection data yet</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(followupAnalytics?.rejection_reasons || {}).map(([code, count]) => (
                      <div key={code} className="flex items-center justify-between">
                        <span className="text-foreground">
                          {followupAnalytics?.rejection_reason_labels?.[code] || code}
                        </span>
                        <span className="font-bold text-foreground">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Survey Ratings */}
              <div className="card-surface p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  Survey Ratings Distribution
                </h3>
                {Object.keys(followupAnalytics?.survey_ratings || {}).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No survey data yet</p>
                ) : (
                  <div className="space-y-3">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const count = followupAnalytics?.survey_ratings?.[String(rating)] || 0;
                      const total = Object.values(followupAnalytics?.survey_ratings || {}).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      const emojis = ["", "😞", "😕", "😐", "🙂", "😄"];
                      
                      return (
                        <div key={rating} className="flex items-center gap-3">
                          <span className="text-2xl w-8">{emojis[rating]}</span>
                          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="w-12 text-right font-medium text-foreground">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Customer Engagement Stats */}
            <div className="card-surface p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Customer Engagement Overview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-foreground">{engagementAnalytics?.total_customers || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{engagementAnalytics?.active_customers || 0}</p>
                  <p className="text-sm text-muted-foreground">Active (30 days)</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">{engagementAnalytics?.email_due || 0}</p>
                  <p className="text-sm text-muted-foreground">Email Re-engage Due</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{engagementAnalytics?.whatsapp_due || 0}</p>
                  <p className="text-sm text-muted-foreground">WhatsApp Due</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Follow-ups Tab */}
        {activeTab === "followups" && (
          <div className="card-surface p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Recent Offer Follow-ups</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Offer #</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Sent</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Valid Until</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Response</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {followups.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        No follow-ups yet. Send an offer to start tracking.
                      </td>
                    </tr>
                  ) : (
                    followups.map((followup) => (
                      <tr key={followup.id} className="border-b border-border hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono font-medium">{followup.offer_number}</td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-foreground">{followup.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{followup.customer_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4"><StatusBadge status={followup.status} /></td>
                        <td className="py-3 px-4 text-sm">{formatDate(followup.offer_sent_at)}</td>
                        <td className="py-3 px-4 text-sm">{formatDate(followup.validity_end)}</td>
                        <td className="py-3 px-4 text-sm capitalize">{followup.customer_response || "—"}</td>
                        <td className="py-3 px-4">
                          {followup.survey_response ? (
                            <RatingDisplay rating={followup.survey_response.rating} />
                          ) : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Engagement Tab */}
        {activeTab === "engagement" && (
          <div className="card-surface p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-foreground">Customer Engagement Tracking</h3>
              <a 
                href="/reactivation" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <Users className="h-4 w-4" />
                Full Reactivation Dashboard
              </a>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Contact</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Inquiries</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Offers</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Confirmed</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Re-engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        No customer engagement data yet.
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer) => (
                      <tr key={customer.id} className="border-b border-border hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-foreground">{customer.customer_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">{customer.customer_email}</p>
                            {customer.company_name && (
                              <p className="text-xs text-muted-foreground">{customer.company_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">{formatTimeAgo(customer.last_contact_at)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            customer.reengagement_status === "active" ? "bg-green-100 text-green-700" :
                            customer.reengagement_status === "email_sent" ? "bg-yellow-100 text-yellow-700" :
                            customer.reengagement_status === "whatsapp_sent" ? "bg-blue-100 text-blue-700" :
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {customer.reengagement_status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">{customer.total_inquiries || 0}</td>
                        <td className="py-3 px-4 text-center">{customer.total_offers_received || 0}</td>
                        <td className="py-3 px-4 text-center text-green-600 font-medium">
                          {customer.total_offers_confirmed || 0}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {customer.email_reminder_sent_at && (
                              <span className="flex items-center gap-1" title={`Email sent ${formatDate(customer.email_reminder_sent_at)}`}>
                                <Mail className="h-3 w-3 text-yellow-500" />
                              </span>
                            )}
                            {customer.whatsapp_reminder_sent_at && (
                              <span className="flex items-center gap-1" title={`WhatsApp sent ${formatDate(customer.whatsapp_reminder_sent_at)}`}>
                                <Phone className="h-3 w-3 text-green-500" />
                              </span>
                            )}
                            {!customer.email_reminder_sent_at && !customer.whatsapp_reminder_sent_at && "—"}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </OffersModuleLayout>
  );
}
